import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useModals } from "@/context/ModalContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Loader2, TrendingUp, ImagePlus, Pencil, Upload } from "lucide-react";
import { fmtPrice, fmtChange, trendColor } from "@/utils/format";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Lookup() {
  const { openStockDetail } = useModals();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const reqId = useRef(0);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      setLoading(false);
      return;
    }
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/market/search", { params: { q } })
        .then((r) => id === reqId.current && setResults(r.data.results))
        .catch(() => id === reqId.current && setResults([]))
        .finally(() => id === reqId.current && setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const pick = (s) => {
    setQ("");
    setResults([]);
    setOpen(false);
    openStockDetail(s.symbol);
  };

  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const typed = q.trim().toUpperCase();
    if (!typed) return;
    const exact = results.find((r) => r.symbol === typed || r.symbol.split(".")[0] === typed);
    pick(exact || results[0] || { symbol: typed });
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid="sidebar-lookup">
      <h3 className="font-heading font-bold text-sm flex items-center gap-1.5 mb-1">
        <TrendingUp className="w-4 h-4 text-primary" /> Quick lookup
      </h3>
      <p className="text-xs text-muted-foreground mb-3">Search any stock or crypto — no account needed.</p>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-testid="sidebar-lookup-input"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          placeholder="AAPL, BTC-USD, TD.TO…"
          className="pl-9 pr-9 font-num"
        />
        {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-muted-foreground" />}
      </div>
      {open && results.length > 0 && (
        <div className="mt-2 max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border" data-testid="sidebar-lookup-results">
          {results.map((s) => (
            <button
              key={s.symbol}
              type="button"
              data-testid={`sidebar-lookup-option-${s.symbol}`}
              onClick={() => pick(s)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-secondary transition-colors"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-num font-semibold text-sm">{s.symbol}</span>
                  <Badge variant="secondary" className="text-[9px]">{s.exchange}</Badge>
                </div>
                <div className="text-xs text-muted-foreground truncate">{s.name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-num text-sm">{fmtPrice(s.price)}</div>
                <div className={`font-num text-xs ${trendColor(s.change_percent)}`}>{fmtChange(s.change, s.change_percent)}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PromoCard({ promo, isAdmin, onEdit }) {
  const hasImg = !!promo.image_url;

  const media = hasImg ? (
    <img src={promo.image_url} alt={promo.title || "Featured"} className="w-full aspect-[4/3] object-cover" />
  ) : (
    <div className="w-full aspect-[4/3] grid place-items-center bg-secondary/40 text-muted-foreground">
      <div className="text-center">
        <ImagePlus className="w-6 h-6 mx-auto" />
        <span className="text-[11px] block mt-1">Add featured spot</span>
      </div>
    </div>
  );

  const body = (
    <div className="relative rounded-2xl border border-border bg-card overflow-hidden group" data-testid={`promo-slot-${promo.slot}`}>
      {media}
      {promo.title && <div className="px-3 py-2 text-xs font-medium leading-snug">{promo.title}</div>}
      {isAdmin && hasImg && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onEdit();
          }}
          data-testid={`promo-edit-${promo.slot}`}
          aria-label="Edit featured spot"
          className="absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full bg-black/60 text-white md:opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Pencil className="w-4 h-4" />
        </button>
      )}
    </div>
  );

  if (hasImg && promo.link_url) {
    return (
      <a
        href={promo.link_url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`promo-link-${promo.slot}`}
        className="block hover:-translate-y-0.5 transition-transform"
      >
        {body}
      </a>
    );
  }
  if (!hasImg && isAdmin) {
    return (
      <button type="button" onClick={onEdit} data-testid={`promo-add-${promo.slot}`} className="block w-full text-left hover:-translate-y-0.5 transition-transform">
        {body}
      </button>
    );
  }
  return body;
}

function PromoEditor({ slot, promo, open, onOpenChange, onSaved }) {
  const [imageUrl, setImageUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setImageUrl(promo?.image_url || "");
    setLinkUrl(promo?.link_url || "");
    setTitle(promo?.title || "");
  }, [open, promo]);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/articles/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(`${BACKEND}${data.url}`);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const persist = async (payload, msg) => {
    setSaving(true);
    try {
      await api.put(`/promos/${slot}`, payload);
      toast.success(msg);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="promo-editor">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Featured spot {slot != null ? slot + 1 : ""}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            data-testid="promo-file-input"
            onChange={(e) => {
              upload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {imageUrl ? (
            <img src={imageUrl} alt="" className="w-full aspect-[4/3] object-cover rounded-xl border border-border" />
          ) : (
            <div className="w-full aspect-[4/3] grid place-items-center rounded-xl border border-dashed border-border text-muted-foreground">
              <ImagePlus className="w-7 h-7" />
            </div>
          )}
          <Button type="button" variant="outline" className="w-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="promo-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Upload className="w-4 h-4 mr-1.5" />} Upload image
          </Button>
          <Input data-testid="promo-image-url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL (or upload above)" />
          <Input data-testid="promo-link-url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="Link URL (https://…)" />
          <Input data-testid="promo-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Caption (optional)" />
          <div className="flex items-center justify-between gap-2 pt-1">
            <Button type="button" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => persist({ image_url: "", link_url: "", title: "" }, "Spot cleared")} disabled={saving} data-testid="promo-clear-btn">
              Clear
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="promo-cancel-btn">Cancel</Button>
              <Button type="button" onClick={() => persist({ image_url: imageUrl, link_url: linkUrl, title }, "Featured spot updated")} disabled={saving} data-testid="promo-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Save
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PromoSpots() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/promos").then((r) => setPromos(r.data.promos)).catch(() => setPromos([]));
  useEffect(() => {
    load();
  }, []);

  const visible = isAdmin ? promos : promos.filter((p) => p.image_url);
  if (!visible.length) return null;

  return (
    <div className="space-y-3" data-testid="sidebar-promos">
      <h3 className="font-heading font-bold text-[11px] uppercase tracking-wider text-muted-foreground">Featured</h3>
      {visible.map((p) => (
        <PromoCard key={p.slot} promo={p} isAdmin={isAdmin} onEdit={() => setEditing(p.slot)} />
      ))}
      <PromoEditor
        slot={editing}
        promo={promos.find((p) => p.slot === editing)}
        open={editing !== null}
        onOpenChange={(o) => !o && setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    </div>
  );
}

export default function ArticleSidebar() {
  return (
    <>
      <Lookup />
      <PromoSpots />
    </>
  );
}
